import React, { useEffect, useState } from 'react';
import {
    Clock,
    Target,
    AlertTriangle,
    XCircle,
    CheckCircle2,
    Flag,
    Shield,
    Coins,
    Handshake,
    EyeOff,
    Lock,
    Quote
} from 'lucide-react';

const PASSWORD = "leos2026";

const Pill = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "bg-indigo-500/10 border-indigo-500/30 text-indigo-300",
        emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
        rose: "bg-rose-500/10 border-rose-500/30 text-rose-300",
        amber: "bg-amber-500/10 border-amber-500/30 text-amber-300",
        slate: "bg-white/5 border-white/10 text-white/70"
    };
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-bold uppercase tracking-wider ${colors[color]}`}>
            {children}
        </span>
    );
};

const NumberCard = ({ label, value, sub, color = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30 text-indigo-300",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30 text-emerald-300",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30 text-rose-300",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30 text-amber-300"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[color]} border rounded-2xl p-5`}>
            <div className="text-xs font-bold uppercase tracking-widest text-white/60 mb-2">{label}</div>
            <div className="text-3xl font-black text-white">{value}</div>
            {sub && <div className="text-xs text-white/50 mt-2 leading-snug">{sub}</div>}
        </div>
    );
};

const AgendaBlock = ({ minute, title, bullets, color = "indigo" }) => {
    const bar = {
        indigo: "border-l-indigo-400",
        emerald: "border-l-emerald-400",
        amber: "border-l-amber-400",
        rose: "border-l-rose-400",
        purple: "border-l-purple-400"
    };
    return (
        <div className={`bg-slate-900/40 border border-white/5 ${bar[color]} border-l-4 rounded-r-xl p-5`}>
            <div className="flex items-center gap-3 mb-3">
                <Clock className="w-4 h-4 text-white/40" />
                <span className="text-xs font-bold uppercase tracking-widest text-white/60">{minute}</span>
            </div>
            <h4 className="text-lg font-bold text-white mb-3">{title}</h4>
            <ul className="space-y-2">
                {bullets.map((b, i) => (
                    <li key={i} className="text-slate-300 text-sm leading-relaxed flex gap-2">
                        <span className="text-white/30 shrink-0">•</span>
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const Quoted = ({ children }) => (
    <div className="bg-indigo-500/5 border-l-2 border-indigo-400/60 pl-4 py-2 italic text-indigo-100/90 text-sm leading-relaxed">
        <Quote className="w-3 h-3 text-indigo-400/60 inline mr-1 -mt-1" />
        {children}
    </div>
);

export default function LeosPrideCallWalkthrough() {

    const [unlocked, setUnlocked] = useState(false);
    const [input, setInput] = useState("");
    const [err, setErr] = useState(false);

    useEffect(() => {
        document.title = "Leo's Pride — Call Walkthrough (Internal)";
        window.scrollTo(0, 0);
    }, []);

    const tryUnlock = (e) => {
        e.preventDefault();
        if (input.trim().toLowerCase() === PASSWORD) {
            setUnlocked(true);
            setErr(false);
        } else {
            setErr(true);
        }
    };

    if (!unlocked) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 pt-24">
                <form onSubmit={tryUnlock} className="max-w-md w-full bg-slate-900/60 backdrop-blur-lg border border-white/10 rounded-2xl p-8 shadow-2xl">
                    <div className="flex items-center gap-3 mb-6">
                        <Lock className="w-6 h-6 text-indigo-400" />
                        <h1 className="text-2xl font-bold text-white">Internal Page</h1>
                    </div>
                    <p className="text-slate-400 text-sm mb-6">
                        Call walkthrough for Suresh only. Not for client sharing.
                    </p>
                    <input
                        type="password"
                        value={input}
                        onChange={(e) => { setInput(e.target.value); setErr(false); }}
                        autoFocus
                        placeholder="Passcode"
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-indigo-400 mb-3"
                    />
                    {err && <p className="text-rose-400 text-sm mb-3">Wrong passcode.</p>}
                    <button
                        type="submit"
                        className="w-full bg-indigo-500 hover:bg-indigo-400 text-white font-bold py-3 rounded-xl transition-colors"
                    >
                        Unlock
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 max-w-5xl mx-auto w-full">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">Leo's Pride Call Walkthrough</span>
                    <span className="ml-auto">
                        <Pill color="rose"><EyeOff className="w-3 h-3" /> Internal</Pill>
                    </span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 space-y-16">

                {/* ───────── HERO ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-4">
                        <Pill color="rose"><EyeOff className="w-3 h-3" /> Suresh Only</Pill>
                        <Pill color="indigo">2026-05-16</Pill>
                        <Pill color="slate">60 min call</Pill>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        The Radha Negotiation Playbook.
                    </h1>
                    <div className="bg-slate-900/60 border border-indigo-500/30 rounded-2xl p-6 md:p-8">
                        <div className="text-indigo-300 text-sm font-bold uppercase tracking-widest mb-3">Goal of this call</div>
                        <ol className="space-y-3 text-slate-200">
                            <li className="flex gap-3"><span className="text-emerald-400 font-bold">1.</span> Close the 30-shorts retainer. Lock it in writing.</li>
                            <li className="flex gap-3"><span className="text-emerald-400 font-bold">2.</span> Align directionally on equity. Don't drafted-contract level. Just principles.</li>
                            <li className="flex gap-3"><span className="text-emerald-400 font-bold">3.</span> Get Radha's Eureka marketing budget number.</li>
                        </ol>
                    </div>
                </section>


                {/* ───────── WALK-AWAY NUMBERS ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Target className="w-6 h-6 text-emerald-400" />
                        <h2 className="text-3xl font-black">Numbers in your head</h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <NumberCard label="Open at" value="$2,500" sub="Month-to-month retainer for 30 shorts" color="indigo" />
                        <NumberCard label="Prepay lever" value="$2,000" sub="3-mo prepay, $6,000 upfront, 20% off" color="emerald" />
                        <NumberCard label="Floor" value="$2,000" sub="Do NOT go below this. AI credits separate." color="amber" />
                        <NumberCard label="Equity ask" value="20%" sub="Open at 25%, settle at 20%. Her offer is 15%." color="rose" />
                    </div>
                    <div className="mt-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5">
                        <div className="text-emerald-300 text-sm font-bold uppercase tracking-widest mb-2">Anchor reference (use in the call)</div>
                        <p className="text-slate-200 leading-relaxed">
                            Last deal: 12 long-form videos at $3,600 ($300/video). This deal: 30 shorts for $2,500 = <strong>~$83/short</strong>.
                        </p>
                        <Quoted>
                            "You got long-form at $300/video. Shorts are 1/4 the length and we're charging you about 1/4 the price per unit, but you get 2.5x the volume per month."
                        </Quoted>
                    </div>
                </section>


                {/* ───────── AGENDA ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-6 h-6 text-indigo-400" />
                        <h2 className="text-3xl font-black">60-minute agenda</h2>
                    </div>
                    <div className="space-y-4">
                        <AgendaBlock
                            minute="0 – 5 min · Warm open"
                            title="Set the frame"
                            color="indigo"
                            bullets={[
                                "\"Today I want to lock in the 30-shorts retainer so we can start production, align on the equity direction, and understand the Eureka marketing budget.\""
                            ]}
                        />
                        <AgendaBlock
                            minute="5 – 25 min · Production deal"
                            title="Present the retainer. Close it."
                            color="emerald"
                            bullets={[
                                "Walk through $2,500/mo (or $2,000 with 3-mo prepay).",
                                "Show the unit economics vs. last deal ($300/video long-form → $83/short).",
                                "Show savings vs. traditional studios ($15K–50K alternative).",
                                "Outline what's included end-to-end.",
                                "Confirm her commitment. Close."
                            ]}
                        />
                        <AgendaBlock
                            minute="25 – 45 min · Equity"
                            title="Directional alignment, not contract drafting"
                            color="amber"
                            bullets={[
                                "Thank her for the structured offer.",
                                "20% (not 15%). Open at 25%, settle at 20%.",
                                "LLC formed within 6 months OR phantom equity against Tech Sprout. Non-negotiable.",
                                "No cliff. Confirm \"counts from day one\" and lock in writing.",
                                "Acceleration: acquisition 100%, termination without cause 50% min, wind-down 100%.",
                                "Minority protections: anti-dilution, tag-along, info rights.",
                                "DEFER the retainer-slide discussion. Scope evolving.",
                                "Raise IP ownership during the interim period (joint with Tech Sprout until LLC).",
                                "Raise Eureka licensing %: must be pinned, not TBD."
                            ]}
                        />
                        <AgendaBlock
                            minute="45 – 55 min · Eureka marketing"
                            title="Gather info, don't quote"
                            color="purple"
                            bullets={[
                                "\"What's your budget thinking for the Eureka digital marketing scope?\"",
                                "Get a range. Don't commit to a price today.",
                                "Propose in next round."
                            ]}
                        />
                        <AgendaBlock
                            minute="55 – 60 min · Close + next steps"
                            title="Lock the actions"
                            color="indigo"
                            bullets={[
                                "Confirm 30 shorts production start date.",
                                "Agree to circulate phantom equity contract draft.",
                                "Agree to next call for Eureka scope + contract review."
                            ]}
                        />
                    </div>
                </section>


                {/* ───────── EQUITY POSITION ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Handshake className="w-6 h-6 text-indigo-400" />
                        <h2 className="text-3xl font-black">Equity position (6 pillars)</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">1. Equity %</div>
                            <p className="text-slate-200">Open at 25%, settle at 20%. Her offer is 15%, she'll likely move.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">2. LLC deadline OR phantom equity</div>
                            <p className="text-slate-200">LLC formed within 6 months OR phantom equity against Tech Sprout that auto-converts when LLC is formed. Non-negotiable in principle.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">3. No cliff</div>
                            <p className="text-slate-200">Confirm her "counts from day one" wording. Lock in writing.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">4. Acceleration</div>
                            <p className="text-slate-200">Acquisition/sale = 100%. Termination without cause = 50% min. Wind-down = 100%.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">5. Minority protections</div>
                            <p className="text-slate-200">Anti-dilution (or pro-rata). Tag-along rights. Information rights. Protection against unilateral changes.</p>
                        </div>
                        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                            <div className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-2">6. Retainer slide → DEFER</div>
                            <p className="text-slate-200">"Scope is still evolving. Shorts, then musicals, then pilot. Let's price per scope and revisit the slide once we know the full monthly workload."</p>
                        </div>
                    </div>
                </section>


                {/* ───────── IP OWNERSHIP RAISE ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Shield className="w-6 h-6 text-amber-400" />
                        <h2 className="text-3xl font-black">CRITICAL: IP ownership during pre-LLC</h2>
                    </div>
                    <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-6 md:p-8">
                        <div className="text-amber-300 text-sm font-bold uppercase tracking-widest mb-3">The question</div>
                        <p className="text-slate-200 leading-relaxed mb-4">
                            Until the Leo's Pride LLC is formed, who legally owns the characters, music, scripts, and footage being produced?
                        </p>
                        <div className="text-amber-300 text-sm font-bold uppercase tracking-widest mb-3 mt-6">Preferred outcome</div>
                        <p className="text-slate-200 leading-relaxed mb-4">
                            Joint ownership between Suresh and Tech Sprout Media until the LLC is formed, at which point both parties transfer ownership into the LLC for agreed equity splits.
                        </p>
                        <div className="text-amber-300 text-sm font-bold uppercase tracking-widest mb-3 mt-6">How to raise it</div>
                        <Quoted>
                            "One thing I want us to think through together. Until the Leo's Pride LLC is formed, who legally owns the IP we're producing? I'd want us to consider joint ownership during this interim period so neither of us is exposed if the LLC takes longer than expected."
                        </Quoted>
                    </div>
                </section>


                {/* ───────── EUREKA LICENSING ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Coins className="w-6 h-6 text-emerald-400" />
                        <h2 className="text-3xl font-black">Eureka licensing terms</h2>
                    </div>
                    <div className="space-y-4">
                        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-5 flex gap-4">
                            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-1" />
                            <div>
                                <div className="font-bold text-white mb-1">Royalty-free biscuit license: ACCEPT</div>
                                <p className="text-slate-300 text-sm">Eureka is funding the early work. The IP exists because of Eureka. Fair trade.</p>
                            </div>
                        </div>
                        <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-5 flex gap-4">
                            <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0 mt-1" />
                            <div>
                                <div className="font-bold text-white mb-1">Third-party licensing share: PIN IT TODAY</div>
                                <ul className="text-slate-300 text-sm space-y-2 mt-2">
                                    <li>• Ask Radha to walk through her reasoning (LLC is separate from Eureka).</li>
                                    <li>• If kept, the % must be pinned in this agreement. Not TBD later.</li>
                                    <li>• Push for a cap. Suggest <strong>10–15% maximum</strong>.</li>
                                    <li>• Clarify: comes out of company gross or after Suresh's 20% is calculated? <strong>Suresh's share first.</strong></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>


                {/* ───────── DON'T BRING UP ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <XCircle className="w-6 h-6 text-rose-400" />
                        <h2 className="text-3xl font-black">Don't bring up today</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-5">
                            <div className="font-bold text-white mb-2">Don't lead with equity</div>
                            <p className="text-slate-300 text-sm">Close the retainer FIRST. Don't let equity friction kill the production deal.</p>
                        </div>
                        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-5">
                            <div className="font-bold text-white mb-2">Don't fight the retainer slide</div>
                            <p className="text-slate-300 text-sm">Defer it. "Scope evolving, let's revisit." Don't make it a confrontation.</p>
                        </div>
                        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-5">
                            <div className="font-bold text-white mb-2">Don't pitch 60-shorts as a hard commit</div>
                            <p className="text-slate-300 text-sm">Plant it as a future option contingent on month 1 results.</p>
                        </div>
                        <div className="bg-rose-500/5 border border-rose-500/30 rounded-xl p-5">
                            <div className="font-bold text-white mb-2">Don't get legal on equity</div>
                            <p className="text-slate-300 text-sm">Today's discussion is "directional alignment". Actual terms get drafted into the contract.</p>
                        </div>
                    </div>
                </section>


                {/* ───────── RED FLAGS ───────── */}
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <Flag className="w-6 h-6 text-rose-400" />
                        <h2 className="text-3xl font-black">Red flags to watch for</h2>
                    </div>
                    <div className="space-y-3">
                        <div className="bg-slate-900/40 border-l-4 border-l-rose-400 border border-white/5 rounded-r-xl p-4">
                            <div className="font-bold text-white mb-1">If she pushes hard against phantom equity</div>
                            <p className="text-slate-300 text-sm">Flag this as the dealbreaker. Don't sign the equity portion without it.</p>
                        </div>
                        <div className="bg-slate-900/40 border-l-4 border-l-rose-400 border border-white/5 rounded-r-xl p-4">
                            <div className="font-bold text-white mb-1">If she refuses to pin Eureka licensing %</div>
                            <p className="text-slate-300 text-sm">Flag. Push for it in the contract.</p>
                        </div>
                        <div className="bg-slate-900/40 border-l-4 border-l-rose-400 border border-white/5 rounded-r-xl p-4">
                            <div className="font-bold text-white mb-1">If she mentions a cliff that wasn't in her message</div>
                            <p className="text-slate-300 text-sm">Push back. Ask for 3–6 months maximum.</p>
                        </div>
                        <div className="bg-slate-900/40 border-l-4 border-l-rose-400 border border-white/5 rounded-r-xl p-4">
                            <div className="font-bold text-white mb-1">If she ties the production deal to current equity terms</div>
                            <p className="text-slate-300 text-sm">Decouple them. "Let's close the production deal independently. Equity is a separate conversation we can take time on."</p>
                        </div>
                    </div>
                </section>


                {/* ───────── CLOSING REMINDER ───────── */}
                <section>
                    <div className="bg-gradient-to-br from-indigo-900/40 to-slate-900/80 border border-indigo-500/30 rounded-3xl p-8 md:p-10 text-center">
                        <Pill color="indigo">Remember</Pill>
                        <h2 className="text-2xl md:text-3xl font-black mt-4 mb-4 text-white">Retainer-heavy, not equity-heavy.</h2>
                        <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto">
                            You're not betting your own capital on this IP. Equity is a bonus, not a paycheck. Trust she's good for the money. Close the retainer. Then negotiate the equity from strength.
                        </p>
                    </div>
                </section>

            </main>
        </div>
    );
}
